import React, { useState } from "react";
import { FooterBase } from "./footer.styled";
import GreenBadge from "../green-badge/green-badge";
import { FooterQuery } from "../../types/graphql";
import { htmlSerializer } from "../../utils/htmlSerializer";
import { PrismicImage, PrismicLink, PrismicRichText } from "@prismicio/react";
import VisibilitySensor from "react-visibility-sensor";
import { FilledImageFieldImage, FilledLinkToWebField } from "@prismicio/types";
import { track } from "../../utils/analytics";

interface IFooterProps {
  data: FooterQuery;
}

const trackFooterLinkClick = (props: {
  link_text?: string;
  link_url?: string;
  link_location: "section" | "social" | "certification";
  column_title?: string;
}) => track("Footer Link Clicked", props);

export const PureFooter = (props: IFooterProps) => {
  const footer = props.data.prismicFooter?.data;
  const sections = footer?.body;
  const emissions = props.data.emissions;
  const [hasViewed, setHasViewed] = useState(false);

  const renderSocialMediaLinks = () => {
    return (
      <ul className="flex gap-4 items-center">
        {footer?.social_media_links?.map((link, index) => (
          <li key={index}>
            <PrismicLink
              field={link.social_media_link as FilledLinkToWebField}
              onClick={() =>
                trackFooterLinkClick({
                  link_text:
                    (link.social_media_icon as FilledImageFieldImage)?.alt ??
                    undefined,
                  link_url: (link.social_media_link as FilledLinkToWebField)
                    ?.url,
                  link_location: "social",
                })
              }
            >
              <PrismicImage
                field={link.social_media_icon as FilledImageFieldImage}
                loading="lazy"
                className="w-9 h-9 opacity-[0.45] hover:opacity-[0.75] transition-opacity duration-300"
              />
            </PrismicLink>
          </li>
        ))}
      </ul>
    );
  };

  const renderSectionLinks = () => {
    return (
      <div className="flex flex-wrap gap-8">
        {sections?.map((section) => (
          <div className="w-full md:w-[calc(33.33%-32px)]" key={section.id}>
            <h5 className="!text-base !font-bold !text-xom-inverted-tertiary">
              {section.primary?.section_title}
            </h5>
            <PrismicRichText
              field={section.primary?.section_content?.richText}
              components={htmlSerializer}
            />
            <ul>
              {section.items?.map((item, index) => (
                <li key={index}>
                  <PrismicLink
                    field={item.link as FilledLinkToWebField}
                    className="!text-sm"
                    onClick={() =>
                      trackFooterLinkClick({
                        link_text: item.title ?? undefined,
                        link_url: (item.link as FilledLinkToWebField)?.url,
                        link_location: "section",
                        column_title:
                          section.primary?.section_title ?? undefined,
                      })
                    }
                  >
                    {item.title}
                  </PrismicLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    );
  };

  const renderCertifications = () => {
    return (
      <>
        {footer?.certifications?.map((certification, index) => (
          <div key={index}>
            <PrismicLink
              field={certification.link as FilledLinkToWebField}
              className="flex gap-1 !text-xs"
              onClick={() =>
                trackFooterLinkClick({
                  link_text: certification.label ?? undefined,
                  link_url: (certification.link as FilledLinkToWebField)?.url,
                  link_location: "certification",
                })
              }
            >
              <PrismicImage
                field={certification.icon as FilledImageFieldImage}
                loading="lazy"
                className="w-4"
              />
              <span>{certification.label}</span>
            </PrismicLink>
          </div>
        ))}
      </>
    );
  };

  return (
    (footer && (
      <FooterBase>
        <VisibilitySensor
          onChange={(isVisible: boolean) => isVisible && setHasViewed(true)}
        >
          <div className="container">
            {/* Footer Top */}
            <div className="!grid grid-cols-12 gap-8">
              <div className="col-span-12 lg:col-span-4 flex flex-col gap-6">
                <div className="flex flex-col gap-4 items-start">
                  {footer?.logo && (
                    <PrismicImage
                      field={footer?.logo as FilledImageFieldImage}
                      loading="lazy"
                      className="max-h-7"
                    />
                  )}
                  <p>{footer?.brand_description}</p>
                </div>
                {renderSocialMediaLinks()}
                {emissions?.total_lbs && hasViewed && (
                  <GreenBadge lbsOffset={emissions.total_lbs} />
                )}
              </div>
              <div className="col-span-12 lg:col-span-8">
                {renderSectionLinks()}
              </div>
            </div>
            <hr className="border-xom-white my-4" />
            {/* Footer Bottom */}
            <div className="flex flex-wrap gap-4 justify-between items-center">
              <div className="flex items-center gap-2 text-xom-inverted-secondary">
                {footer?.logo_mark && (
                  <PrismicImage
                    field={footer?.logo_mark as FilledImageFieldImage}
                    loading="lazy"
                    className="inline-block w-8 h-8"
                  />
                )}
                <span>
                  &copy; {new Date().getFullYear()} {footer?.copyright}
                </span>
              </div>
              <div className="flex flex-wrap gap-4">
                {renderCertifications()}
              </div>
            </div>
          </div>
        </VisibilitySensor>
      </FooterBase>
    )) || <></>
  );
};
